import { getGameInfoByIdPlus } from '@intelligence/services/common';
import { getUrlObject } from '@home/utils/urlConfig';
import {
  createRouteOnlyAnalysisUrlInfoHandler,
  flattenUrlCollectionRoutes,
  getUrlCollectionBusinessPageNameByUrlObject,
  getBaseUrlCollectionSuccessResult,
  getDefaultUrlCollectionAnalysisUrlInfoResult,
  getUrlCollectionEntityDetailsResult,
  getUrlCollectionEntityPageName,
  getUrlCollectionEntityTypes,
  getUrlCollectionPathSegments,
  getUrlCollectionSystemPathSegments,
} from '@home/utils/urlCollectionCommon';
import { mergeUrlCollectionData, parseUrlCollectionData } from '@home/utils/urlCollectionData';
import { EntityType, URL_COLLECTION_ANALYSIS_FAILURE_REASON } from '@home/enums/urlCollection';
import { DBSystemCode } from '@home/_config/config';
import routers from '@intelligence/_config/routers';

import type { IAnalysisUrlInfoResult } from '@home/enums/urlCollection';
import type {
  IRestoreDefaultUrlCollectionItemParams,
  IRestoreDefaultUrlCollectionItemResult,
} from '@home/types/urlCollection';

// 通用debounce配置
export const DEBOUNCE_CONFIG = { leading: true, trailing: false, wait: 600 };

/** intelligence 详情页路径片段与实体类型映射 */
const INTELLIGENCE_GAME_ENTITY_TYPE_MAP: Record<string, EntityType> = {
  mobileGames: EntityType.Mobile,
  pcGames: EntityType.PC,
  consoleGames: EntityType.Console,
  pcConsoleGames: EntityType.Combine,
};

const GAME_NAME_FIELD = 'entity_name';
const GAME_COVER_FIELD = 'cover';
const ENTITY_TYPE_FIELD = 'entity_type';
const GAME_ENTITY_CUSTOM_FIELDS = [ENTITY_TYPE_FIELD, GAME_NAME_FIELD, GAME_COVER_FIELD];
const COMPANY_NAME_FIELD = 'name';
const COMPANY_COVER_FIELD = 'image_url';
const COMPANY_ENTITY_CUSTOM_FIELDS = [ENTITY_TYPE_FIELD, COMPANY_NAME_FIELD, COMPANY_COVER_FIELD];
const INTELLIGENCE_ROUTE_PATHS = flattenUrlCollectionRoutes(routers).filter((path) => {
  return !path.includes('*');
});
const INTELLIGENCE_DEFAULT_SHORTCUT_URL_MAP: Record<string, string> = {
  mobile: '/v2/intelligence/topCharts/mobileGames/gamesDetails/:entityId/stats/activeusers',
  pc: '/v2/intelligence/topCharts/pcGames/gamesDetails/:entityId/stats',
  console: '/v2/intelligence/topCharts/consoleGames/gamesDetails/:entityId/stats',
  combine: '/v2/intelligence/topCharts/pcConsoleGames/gamesDetails/:entityId/stats',
};

/** 统一拼装实体结果 */
const getEntityDetailsResult = (
  entityType: EntityType | '',
  entityId: string,
  entityInfo?: { entity_type?: unknown; entity_name: string; cover: string } | null,
): Pick<IAnalysisUrlInfoResult, 'entity_type' | 'entity_id' | 'entity_name' | 'entity_cover'> => {
  const normalizedEntityTypes = getUrlCollectionEntityTypes(entityInfo?.entity_type);

  return getUrlCollectionEntityDetailsResult({
    entityType: normalizedEntityTypes.length ? normalizedEntityTypes : entityType,
    entityId,
    entityName: entityInfo?.entity_name || '',
    entityCover: entityInfo?.cover || '',
  });
};

const getIntelligenceDetailPageSuffix = (systemPathSegments: string[]): string => {
  return systemPathSegments
    .slice(4)
    .map((segment) => {
      if (!segment) {
        return '';
      }

      return `${segment.charAt(0).toUpperCase()}${segment.slice(1)}`;
    })
    .filter(Boolean)
    .join(' ');
};

const getIntelligenceEntityDetailPageName = (
  pageName: string,
  entityName: string,
  detailPageSuffix: string,
): string => {
  const basePageName = getUrlCollectionEntityPageName(pageName, entityName);

  if (!detailPageSuffix) {
    return basePageName;
  }

  return `${basePageName} ${detailPageSuffix}`;
};

/** 从 intelligence 业务路径中识别详情页实体信息 */
const getDetailPageInfo = (systemPathSegments: string[]): { entityType: EntityType; entityId: string } | null => {
  const [moduleName, entityName, detailKey, entityId] = systemPathSegments;

  if (moduleName !== 'topCharts' || detailKey !== 'gamesDetails' || !entityId) {
    return null;
  }

  if (entityName === 'companies') {
    return {
      entityType: EntityType.Company,
      entityId,
    };
  }

  const entityType = INTELLIGENCE_GAME_ENTITY_TYPE_MAP[entityName];

  if (!entityType) {
    return null;
  }

  return {
    entityType,
    entityId,
  };
};

/** 根据实体类型和实体 id 拉取详情信息 */
const getEntityInfoById = async (
  entityType: EntityType | '',
  entityId: string,
): Promise<{ entity_type?: unknown; entity_name: string; cover: string } | null> => {
  if (!entityId) {
    return null;
  }

  let customFields = GAME_ENTITY_CUSTOM_FIELDS;
  let entityNameField = GAME_NAME_FIELD;
  let coverField = GAME_COVER_FIELD;

  if (entityType === EntityType.Company) {
    customFields = COMPANY_ENTITY_CUSTOM_FIELDS;
    entityNameField = COMPANY_NAME_FIELD;
    coverField = COMPANY_COVER_FIELD;
  }

  const res = await getGameInfoByIdPlus({
    entity_type: entityType || 'auto',
    level: 'custom',
    custom_fields: customFields,
    ids: [entityId],
  });
  const entityDetails = res?.data?.[entityId];
  const nextEntityType = entityDetails?.[ENTITY_TYPE_FIELD];
  const entityName = entityDetails?.[entityNameField];
  const cover = entityDetails?.[coverField];

  return { entity_type: nextEntityType, entity_name: entityName, cover };
};

/**
 * intelligence 子系统收藏 URL 解析入口。
 *
 * 非详情页仅返回页面信息；详情页会进一步补充实体信息。
 */
export const analysisUrlInfo = createRouteOnlyAnalysisUrlInfoHandler({
  systemCode: DBSystemCode.Intelligence,
  routePaths: INTELLIGENCE_ROUTE_PATHS,
  getPageName: (urlObj: URL | null): string => {
    return getUrlCollectionBusinessPageNameByUrlObject(urlObj);
  },
  getResult: async ({ url, pagePath, urlObj, baseSuccessResult }): Promise<IAnalysisUrlInfoResult | null> => {
    const pathSegments = getUrlCollectionPathSegments(urlObj);
    const systemPathSegments = getUrlCollectionSystemPathSegments(pathSegments);
    const detailPageInfo = getDetailPageInfo(systemPathSegments);
    const pageName = baseSuccessResult.page_name;
    const detailPageSuffix = getIntelligenceDetailPageSuffix(systemPathSegments);

    if (!detailPageInfo) {
      return null;
    }

    try {
      const entityInfo = await getEntityInfoById(detailPageInfo.entityType, detailPageInfo.entityId);

      if (!entityInfo) {
        return {
          ...getDefaultUrlCollectionAnalysisUrlInfoResult(url, urlObj, {
            permission: baseSuccessResult.permission,
            status: baseSuccessResult.status,
            failureReason: URL_COLLECTION_ANALYSIS_FAILURE_REASON.Unavailable,
            system: DBSystemCode.Intelligence,
            pagePath,
          }),
          ...getEntityDetailsResult(detailPageInfo.entityType, detailPageInfo.entityId),
          page_name: getUrlCollectionEntityPageName(pageName),
        };
      }

      return {
        ...baseSuccessResult,
        ...getEntityDetailsResult(detailPageInfo.entityType, detailPageInfo.entityId, entityInfo),
        page_name: getIntelligenceEntityDetailPageName(pageName, entityInfo.entity_name || '', detailPageSuffix),
      };
    } catch (error) {
      return {
        ...getDefaultUrlCollectionAnalysisUrlInfoResult(url, urlObj, {
          permission: baseSuccessResult.permission,
          status: baseSuccessResult.status,
          failureReason: URL_COLLECTION_ANALYSIS_FAILURE_REASON.Unavailable,
          system: DBSystemCode.Intelligence,
          pagePath,
        }),
        ...getEntityDetailsResult(detailPageInfo.entityType, detailPageInfo.entityId),
        page_name: getUrlCollectionEntityPageName(pageName),
      };
    }
  },
});

/**
 * 根据实体信息查询最新的实体详情
 * @param params 收藏项
 * @return 收藏项对应的实体信息
 */
export const getEntityDetailsById = async (
  params: IAnalysisUrlInfoResult,
): Promise<Pick<IAnalysisUrlInfoResult, 'entity_type' | 'entity_id' | 'entity_name' | 'entity_cover'>> => {
  const entityType = params?.entity_type?.[0] || '';
  const entityId = params?.entity_id || '';

  if (!entityId) {
    return getEntityDetailsResult('', '');
  }

  try {
    const entityInfo = await getEntityInfoById(entityType, entityId);

    return getEntityDetailsResult(entityType, entityId, entityInfo);
  } catch (error) {
    return getEntityDetailsResult(entityType, entityId);
  }
};

export const restoreDefaultShortcutItem = async (
  params: IRestoreDefaultUrlCollectionItemParams,
): Promise<IRestoreDefaultUrlCollectionItemResult> => {
  const entityId = params?.entity_id;

  if (!entityId) {
    return {};
  }

  const nextEntityType = params?.entity_type;
  const urlTemplate = nextEntityType ? INTELLIGENCE_DEFAULT_SHORTCUT_URL_MAP[nextEntityType] : '';

  if (!urlTemplate) {
    return {};
  }

  const nextUrl = urlTemplate.replace(':entityId', entityId);
  const nextUrlObj = getUrlObject(nextUrl);
  const baseSuccessResult = getBaseUrlCollectionSuccessResult(nextUrl, nextUrlObj, {
    system: DBSystemCode.Intelligence,
    pagePath: nextUrl,
  });
  const pathSegments = getUrlCollectionPathSegments(nextUrlObj);
  const systemPathSegments = getUrlCollectionSystemPathSegments(pathSegments);
  const detailPageSuffix = getIntelligenceDetailPageSuffix(systemPathSegments);
  const entityName = params.entity_name || '';
  const nextName = entityName
    ? getIntelligenceEntityDetailPageName(baseSuccessResult.page_name, entityName, detailPageSuffix)
    : params.name;
  const nextData = parseUrlCollectionData(params.data);

  return {
    url: nextUrl,
    name: nextName || params.name,
    data: mergeUrlCollectionData(params.data, {
      ...nextData,
      entity_type: nextEntityType ? [nextEntityType] : [],
      entity_id: params.entity_id,
      entity_name: entityName,
      entity_cover: params.entity_cover,
      page_name: nextName || nextData.page_name || params.name,
      page_path: baseSuccessResult.page_path || nextData.page_path || '',
    }),
  };
};
